import type {
  DynamicPricingGapPolicy,
  DynamicPricingMode,
  PricingConfigurationInput,
} from '@/shared/api';
import { pricingConfigurationFormSchema } from './pricingConfigurationRules';

export { pricingConfigurationFormSchema };

export interface PricingConfigurationFormValues {
  mode: DynamicPricingMode;
  minimumPrice: string;
  referencePrice: string;
  maximumPrice: string;
  strategyProfileVersionId: string;
  regularMinimumStay: string;
  fridaySaturdayMinimumStay: string;
  minimumStayTiersEnabled: boolean;
  minimumStayLeadTimeTiers: Array<{ daysBeforeArrival: string; minimumStayNights: string }>;
  maximumGapLength: string;
  gapPolicy: DynamicPricingGapPolicy;
  gapWithinDays: string;
  lastMinuteDiscountEnabled: boolean;
  gapDiscountEnabled: boolean;
  calculationIntervalHours: string;
  calculationStartLocalTime: string;
  publicationWindows: Array<{ startLocalTime: string; endLocalTime: string }>;
}

const shortTime = (entry: string) => entry.slice(0, 5);
const value = (entry: number | null) => (entry === null ? '' : String(entry));

export function toFormValues(input: PricingConfigurationInput): PricingConfigurationFormValues {
  return {
    mode: input.mode,
    minimumPrice: value(input.minimumPrice),
    referencePrice: value(input.referencePrice),
    maximumPrice: value(input.maximumPrice),
    strategyProfileVersionId: input.strategyProfileVersionId ?? '',
    regularMinimumStay: value(input.regularMinimumStay),
    fridaySaturdayMinimumStay: value(input.fridaySaturdayMinimumStay),
    minimumStayTiersEnabled: input.minimumStayTiersEnabled,
    minimumStayLeadTimeTiers: input.minimumStayLeadTimeTiers.map((tier) => ({
      daysBeforeArrival: String(tier.daysBeforeArrival),
      minimumStayNights: String(tier.minimumStayNights),
    })),
    maximumGapLength: String(input.maximumGapLength),
    gapPolicy: input.gapPolicy,
    gapWithinDays: value(input.gapWithinDays),
    lastMinuteDiscountEnabled: input.lastMinuteDiscountEnabled,
    gapDiscountEnabled: input.gapDiscountEnabled,
    calculationIntervalHours: String(input.calculationIntervalHours),
    calculationStartLocalTime: shortTime(input.calculationStartLocalTime),
    publicationWindows: input.publicationWindows.map((window) => ({
      startLocalTime: shortTime(window.startLocalTime),
      endLocalTime: shortTime(window.endLocalTime),
    })),
  };
}

export function toApiInput(
  values: PricingConfigurationFormValues,
  source: Pick<PricingConfigurationInput, 'currency' | 'horizonDays'>,
): PricingConfigurationInput {
  const parsed = pricingConfigurationFormSchema.parse(values);
  const number = (entry: string) => (entry === '' ? null : Number(entry));
  return {
    mode: parsed.mode,
    currency: source.currency,
    minimumPrice: number(parsed.minimumPrice),
    referencePrice: number(parsed.referencePrice),
    maximumPrice: number(parsed.maximumPrice),
    strategyProfileVersionId: parsed.strategyProfileVersionId || null,
    regularMinimumStay: number(parsed.regularMinimumStay),
    fridaySaturdayMinimumStay: number(parsed.fridaySaturdayMinimumStay),
    minimumStayTiersEnabled: parsed.minimumStayTiersEnabled,
    minimumStayLeadTimeTiers: parsed.minimumStayLeadTimeTiers
      .map((tier) => ({
        daysBeforeArrival: Number(tier.daysBeforeArrival),
        minimumStayNights: Number(tier.minimumStayNights),
      }))
      .sort((left, right) => left.daysBeforeArrival - right.daysBeforeArrival),
    maximumGapLength: Number(parsed.maximumGapLength),
    gapPolicy: parsed.gapPolicy,
    gapWithinDays: parsed.gapPolicy === 'WithinDays' ? number(parsed.gapWithinDays) : null,
    lastMinuteDiscountEnabled: parsed.lastMinuteDiscountEnabled,
    gapDiscountEnabled: parsed.gapDiscountEnabled,
    calculationIntervalHours: Number(parsed.calculationIntervalHours),
    calculationStartLocalTime: `${parsed.calculationStartLocalTime}:00`,
    publicationWindows: parsed.publicationWindows.map((window) => ({
      startLocalTime: `${window.startLocalTime}:00`,
      endLocalTime: `${window.endLocalTime}:00`,
    })),
    horizonDays: source.horizonDays,
  };
}
